import React, { useMemo, useState } from "react";
import { getApiErrorMessage } from "../../api";
import { formatMoney } from "./financeUtils";

const KIND_LABELS = {
  service: "Услуги",
  kontur: "Контур",
  expense: "Расход",
  liability: "Кредит",
  payroll: "Зарплата",
  dismiss: "Скрыть",
};

const CREDIT_ONLY = new Set(["service", "kontur"]);
const DEBIT_ONLY = new Set(["expense", "liability", "payroll"]);

export function kindsForDirection(kinds, direction) {
  return (kinds || []).filter((kind) => {
    if (kind === "dismiss") return true;
    if (direction === "debit") return DEBIT_ONLY.has(kind);
    if (direction === "credit") return CREDIT_ONLY.has(kind);
    return false;
  });
}

export function signedBankAmount(item) {
  const num = Number(item?.amount);
  if (!Number.isFinite(num)) return "—";
  const signed = item.direction === "debit" ? -Math.abs(num) : Math.abs(num);
  return formatMoney(signed);
}

export function formatBankDate(value) {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-");
  if (!year || !month || !day) return raw;
  return `${day}.${month}.${year}`;
}

function buildResolveBody(kind, form) {
  if (kind === "service") {
    if (!form.clientId || !/^\d{4}-\d{2}$/.test(form.period)) return null;
    return { kind, client_id: Number(form.clientId), period: form.period };
  }
  if (kind === "kontur") {
    if (!form.clientId) return null;
    return { kind, client_id: Number(form.clientId) };
  }
  if (kind === "expense") {
    if (!form.expenseId) return null;
    return { kind, expense_id: Number(form.expenseId) };
  }
  if (kind === "liability") {
    if (!form.liabilityId) return null;
    return { kind, liability_id: Number(form.liabilityId) };
  }
  if (kind === "payroll") {
    if (!form.employeeId) return null;
    return { kind, employee_id: Number(form.employeeId) };
  }
  if (kind === "dismiss") {
    return { kind };
  }
  return null;
}

export default function BankInboxRow({ item, kinds, options, onResolve }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("");
  const [clientId, setClientId] = useState("");
  const [period, setPeriod] = useState("");
  const [expenseId, setExpenseId] = useState("");
  const [liabilityId, setLiabilityId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const visibleKinds = useMemo(
    () => kindsForDirection(kinds, item.direction),
    [kinds, item.direction]
  );
  const body = buildResolveBody(kind, {
    clientId,
    period,
    expenseId,
    liabilityId,
    employeeId,
  });

  const resetForm = () => {
    setKind("");
    setClientId("");
    setPeriod("");
    setExpenseId("");
    setLiabilityId("");
    setEmployeeId("");
    setError("");
  };

  const toggleForm = () => {
    setOpen((prev) => {
      if (prev) resetForm();
      return !prev;
    });
  };

  const submit = async (payload) => {
    if (payload.kind === "dismiss" && !window.confirm("Скрыть эту операцию?")) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onResolve(item.id, payload);
      setOpen(false);
      resetForm();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось записать"));
    } finally {
      setSaving(false);
    }
  };

  const accountBits = [item.owner_label, item.account_tail ? `…${item.account_tail}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="cp-inbox-row">
      <div className="cp-inbox-main">
        <div className="cp-inbox-payer">{item.counterparty_name || "Контрагент"}</div>
        <div className="cp-inbox-meta">
          <span>{formatBankDate(item.paid_at)}</span>
          {accountBits ? <span> · {accountBits}</span> : null}
          {item.counterparty_inn ? <span> · ИНН {item.counterparty_inn}</span> : null}
        </div>
        {item.purpose ? <div className="cp-inbox-purpose">{item.purpose}</div> : null}
      </div>
      <div className="cp-inbox-side">
        <div className="cp-inbox-amount">{signedBankAmount(item)}</div>
        <div className="cp-inbox-side-meta">
          <span className="cd-badge cp-inbox-reason">{item.reason_label}</span>
          <button type="button" className="cd-sort-btn" onClick={toggleForm}>
            Разобрать
          </button>
        </div>
      </div>
      {open && (
        <form
          className="fin-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (body) submit(body);
          }}
        >
          <label className="fin-kpi-caption">
            Тип
            <select
              className="login-input"
              aria-label="Тип"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              <option value="">Выберите тип</option>
              {visibleKinds.map((value) => (
                <option key={value} value={value}>{KIND_LABELS[value] || value}</option>
              ))}
            </select>
          </label>
          {(kind === "service" || kind === "kontur") && (
            <label className="fin-kpi-caption">
              Клиент
              <select
                className="login-input"
                aria-label="Клиент"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              >
                <option value="">Выберите клиента</option>
                {(options?.clients || []).map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </label>
          )}
          {kind === "service" && (
            <label className="fin-kpi-caption">
              Месяц
              <input
                className="login-input"
                type="month"
                aria-label="Месяц"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
              />
            </label>
          )}
          {kind === "expense" && (
            <label className="fin-kpi-caption">
              Расход
              <select
                className="login-input"
                aria-label="Расход"
                value={expenseId}
                onChange={(event) => setExpenseId(event.target.value)}
              >
                <option value="">Выберите расход</option>
                {(options?.expenses || []).map((expense) => (
                  <option key={expense.id} value={expense.id}>{expense.title}</option>
                ))}
              </select>
            </label>
          )}
          {kind === "liability" && (
            <label className="fin-kpi-caption">
              Кредит
              <select
                className="login-input"
                aria-label="Кредит"
                value={liabilityId}
                onChange={(event) => setLiabilityId(event.target.value)}
              >
                <option value="">Выберите кредит</option>
                {(options?.liabilities || []).map((loan) => (
                  <option key={loan.id} value={loan.id}>{loan.title}</option>
                ))}
              </select>
            </label>
          )}
          {kind === "payroll" && (
            <label className="fin-kpi-caption">
              Сотрудник
              <select
                className="login-input"
                aria-label="Сотрудник"
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
              >
                <option value="">Выберите сотрудника</option>
                {(options?.employees || []).map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name}</option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className="cd-sort-btn" disabled={!body || saving}>
            Записать
          </button>
          <button
            type="button"
            className="cd-filter-reset"
            disabled={saving}
            onClick={() => submit({ kind: "dismiss" })}
          >
            Скрыть
          </button>
          {error ? (
            <div className="login-error" role="status" aria-live="polite">{error}</div>
          ) : null}
        </form>
      )}
    </li>
  );
}
